import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { formatGermanDateFromTimestamp, msUntil } from '../lib/date-format';

interface Objekt {
  id: string;
  kundeId: string;
  objektnummer: string | null;
  bezeichnung: string;
  adresseStrasse: string | null;
  adressePlz: string | null;
  adresseOrt: string | null;
  adresseLand: string;
  typ: string | null;
  anzahlEinheiten: number | null;
  // Slice 3 (Migration 0056)
  anzahlEingaenge: number;
  aussenflaecheQm: string | null;
  gruenflaecheQm: string | null;
  winterdienstflaecheQm: string | null;
  hatAufzug: boolean;
  baujahr: number | null;
  // BV-Pricing-Eigenschaften (Migration 0057)
  flurgroesseKlasse: 'klein' | 'mittel' | 'gross' | null;
  hausGroesseKlasse: 'klein' | 'mittel' | 'gross' | null;
  reinigungProWoche: string | null;
  eigentuemerKundeId: string | null;
  hausverwaltungKundeId: string | null;
  rechnungsempfaengerTyp: 'kunde' | 'eigentuemer' | 'hausverwaltung' | 'freie_anschrift';
  rechnungCoZeile: string | null;
  rechnungAnschriftName: string | null;
  rechnungAnschriftZusatz: string | null;
  rechnungAnschriftStrasse: string | null;
  rechnungAnschriftPlz: string | null;
  rechnungAnschriftOrt: string | null;
  rechnungAnschriftLand: string | null;
  ccMails: string[];
  notizen: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Eingang {
  id: string;
  objektId: string;
  reihenfolge: number;
  bezeichnung: string | null;
  flaecheQm: string | null;
  etagen: number | null;
  notizen: string | null;
}

type ObjektTabId =
  | 'stammdaten'
  | 'rechnungsadresse'
  | 'eingaenge'
  | 'ccmails'
  | 'kontakte'
  | 'qr'
  | 'anfragen';

const TAB_LABELS: Record<ObjektTabId, string> = {
  stammdaten: 'Stammdaten',
  rechnungsadresse: 'Rechnungsadresse',
  eingaenge: 'Eingänge & Flächen',
  ccmails: 'CC-Mails',
  kontakte: 'Kontakte',
  qr: 'QR-Aushänge',
  anfragen: 'Anfragen',
};

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

interface Kontakt {
  id: string;
  objektId: string | null;
  vorname: string | null;
  nachname: string;
  rolle: string | null;
  email: string | null;
  telefon: string | null;
  mobil: string | null;
  istHauptkontakt: boolean;
}

export const Route = createFileRoute('/objekte/$id')({
  beforeLoad: () => requireAccess('/api/v1/objekte'),
  component: ObjektDetailPage,
});

function ObjektDetailPage() {
  const { id } = Route.useParams();
  const [activeTab, setActiveTab] = useState<ObjektTabId>('stammdaten');
  const objektQ = useQuery({
    queryKey: ['objekt', id],
    queryFn: async () => {
      try {
        return await api<{ item: Objekt }>(`/api/v1/objekte/${id}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
  const kundeQ = useQuery({
    queryKey: ['kunde', objektQ.data?.item.kundeId],
    queryFn: () => api<{ item: Kunde }>(`/api/v1/kunden/${objektQ.data!.item.kundeId}`),
    enabled: !!objektQ.data?.item.kundeId,
  });
  const kontakteQ = useQuery({
    queryKey: ['kontakte', 'objekt', id],
    queryFn: () =>
      api<{ items: Kontakt[] }>(`/api/v1/kontakte?kundeId=${objektQ.data!.item.kundeId}`),
    enabled: !!objektQ.data?.item.kundeId,
  });
  const anfragenQ = useQuery({
    queryKey: ['inbox', 'objekt', id],
    queryFn: () => api<{ items: ObjektAnfrage[] }>(`/api/v1/inbox?objektId=${id}&limit=50`),
    enabled: objektQ.data != null,
  });

  if (objektQ.isLoading) return <div className="text-slate-500">Lade…</div>;
  if (objektQ.error) return <ErrorBanner error={objektQ.error} />;
  if (!objektQ.data) {
    return (
      <div className="space-y-4">
        <Link to="/objekte" className="text-sm text-brand-700 hover:underline">
          ← zurück zur Objekteliste
        </Link>
        <div className="rounded-md border border-slate-200 bg-white p-6 text-slate-500">
          Objekt nicht gefunden.
        </div>
      </div>
    );
  }
  const objekt = objektQ.data.item;
  const objektKontakte = (kontakteQ.data?.items ?? []).filter((k) => k.objektId === objekt.id);
  const sonstigeKontakte = (kontakteQ.data?.items ?? []).filter((k) => k.objektId !== objekt.id);

  const TAB_IDS: ObjektTabId[] = [
    'stammdaten',
    'rechnungsadresse',
    'eingaenge',
    'ccmails',
    'kontakte',
    'qr',
    'anfragen',
  ];

  return (
    <div className="space-y-6">
      <div>
        <Link to="/objekte" className="text-sm text-brand-700 hover:underline">
          ← zurück zur Objekteliste
        </Link>
      </div>
      <PageHeader
        title={objekt.bezeichnung}
        description={objekt.objektnummer ? `Objekt-Nr. ${objekt.objektnummer}` : 'Kein Objekt-Code'}
      />

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex flex-wrap gap-x-6" role="tablist">
          {TAB_IDS.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={activeTab === t}
              onClick={() => setActiveTab(t)}
              className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium transition-colors ${
                activeTab === t
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'stammdaten' && (
        <ObjektStammdatenPanel objekt={objekt} kunde={kundeQ.data?.item ?? null} />
      )}

      {activeTab === 'rechnungsadresse' && <ObjektRechnungsadressePanel objekt={objekt} />}

      {activeTab === 'eingaenge' && <ObjektEingaengePanel objekt={objekt} />}

      {activeTab === 'ccmails' && <ObjektCcMailsPanel objekt={objekt} />}

      {activeTab === 'kontakte' && (
        <>
          <Panel title={`Kontakte am Objekt (${objektKontakte.length})`}>
            {objektKontakte.length === 0 ? (
              <div className="text-slate-400 text-sm">
                Keine Kontakte direkt am Objekt verknüpft.
              </div>
            ) : (
              <KontakteList kontakte={objektKontakte} />
            )}
          </Panel>
          <Panel title={`Weitere Kontakte des Kunden (${sonstigeKontakte.length})`}>
            {sonstigeKontakte.length === 0 ? (
              <div className="text-slate-400 text-sm">–</div>
            ) : (
              <KontakteList kontakte={sonstigeKontakte} />
            )}
          </Panel>
        </>
      )}

      {activeTab === 'qr' && <ObjektQrPanel objektId={id} />}

      {activeTab === 'anfragen' && (
        <ObjektAnfragenPanel
          anfragen={anfragenQ.data?.items ?? []}
          loading={anfragenQ.isLoading}
          error={anfragenQ.error}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// QR-Token-Panel (Schritt 5.UI.3): Buero erzeugt + druckt + widerruft QR-Codes
// fuer den Mieter-/Bewohner-Aushang im Treppenhaus.
// ---------------------------------------------------------------------------

interface QrToken {
  id: string;
  objektId: string;
  printedAt: string;
  expiresAt: string;
  revokedAt: string | null;
  revokedReason: string | null;
}

function ObjektQrPanel({ objektId }: { objektId: string }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['qr-tokens', objektId],
    queryFn: () => api<{ items: QrToken[] }>(`/api/v1/objekte/${objektId}/qr-tokens`),
  });

  // Plaintext nach Erzeugen einmal kurz im UI halten (zum Drucken). Wird beim
  // Schliessen aus dem State entfernt — nirgendwo persistiert.
  const [freshToken, setFreshToken] = useState<{ id: string; plaintext: string } | null>(null);
  const [error, setError] = useState<unknown>(null);

  const create = useMutation({
    mutationFn: () =>
      api<{ item: QrToken; plaintextToken: string }>(`/api/v1/objekte/${objektId}/qr-tokens`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: (r) => {
      setFreshToken({ id: r.item.id, plaintext: r.plaintextToken });
      void qc.invalidateQueries({ queryKey: ['qr-tokens', objektId] });
    },
    onError: (err) => setError(err),
  });

  const aktive = list.data?.items.filter((t) => !t.revokedAt) ?? [];
  const widerrufen = list.data?.items.filter((t) => t.revokedAt) ?? [];

  return (
    <Panel
      title={`QR-Aushaenge (${aktive.length} aktiv)`}
      actions={
        <Button onClick={() => create.mutate()} disabled={create.isPending}>
          {create.isPending ? 'Erzeuge…' : '+ Neuer QR-Aushang'}
        </Button>
      }
    >
      <ErrorBanner error={error} />

      {freshToken && (
        <FreshTokenBox tokenPlaintext={freshToken.plaintext} onClose={() => setFreshToken(null)} />
      )}

      {list.isLoading && <div className="text-slate-500 text-sm">Lade…</div>}
      <ErrorBanner error={list.error} />

      {!list.isLoading && aktive.length === 0 && !freshToken && (
        <p className="text-sm text-slate-400">
          Kein aktiver QR-Aushang. „+ Neuer QR-Aushang" erzeugt einen Token (24 Monate Lebensdauer)
          und liefert ein druckfertiges A4-PDF.
        </p>
      )}

      {aktive.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2 py-1 text-left">Erzeugt am</th>
              <th className="px-2 py-1 text-left">Gueltig bis</th>
              <th className="px-2 py-1"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {aktive.map((t) => (
              <QrTokenRow key={t.id} token={t} />
            ))}
          </tbody>
        </table>
      )}

      {widerrufen.length > 0 && (
        <details className="text-xs text-slate-500 mt-3">
          <summary className="cursor-pointer">
            {widerrufen.length} widerrufene Tokens (Historie)
          </summary>
          <ul className="mt-2 space-y-1">
            {widerrufen.map((t) => (
              <li key={t.id}>
                {fmtDateShort(t.printedAt)} → {fmtDateShort(t.revokedAt!)}: {t.revokedReason ?? '–'}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Panel>
  );
}

function FreshTokenBox({
  tokenPlaintext,
  onClose,
}: {
  tokenPlaintext: string;
  onClose: () => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function downloadPdf() {
    setError(null);
    setDownloading(true);
    try {
      const csrf = document.cookie
        .split(';')
        .map((c) => c.trim())
        .find((c) => c.startsWith('busch_erp_csrf='))
        ?.split('=')[1];
      const res = await fetch('/api/v1/qr-tokens/druck-pdf', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': decodeURIComponent(csrf) } : {}),
        },
        body: JSON.stringify({ token: tokenPlaintext }),
      });
      if (!res.ok) {
        throw new Error(`PDF-Download fehlgeschlagen (HTTP ${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-aushang-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-amber-900">QR-Token erzeugt</h3>
          <p className="text-xs text-amber-800 mt-1">
            Drucke den Aushang JETZT — der Token-Plaintext ist nur in dieser Sitzung sichtbar und
            kann nicht erneut abgerufen werden. Nach dem Schliessen bleibt nur der Hash in der DB.
          </p>
        </div>
        <Button variant="ghost" onClick={onClose}>
          Schliessen
        </Button>
      </div>

      <div className="rounded bg-white border border-amber-200 p-2">
        <div className="text-xs text-slate-500">Token (Plaintext, einmalig sichtbar)</div>
        <div className="font-mono text-xs break-all mt-1">{tokenPlaintext}</div>
      </div>

      <ErrorBanner error={error} />

      <div className="flex justify-end gap-2">
        <Button onClick={downloadPdf} disabled={downloading}>
          {downloading ? 'Lade PDF…' : 'A4-Aushang als PDF runterladen'}
        </Button>
      </div>
    </div>
  );
}

function QrTokenRow({ token }: { token: QrToken }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [askReason, setAskReason] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const revoke = useMutation({
    mutationFn: () =>
      api(`/api/v1/qr-tokens/${token.id}/revoke`, {
        method: 'POST',
        body: { reason: reason || 'Widerrufen ueber ERP-UI' },
      }),
    onSuccess: () => {
      setAskReason(false);
      setReason('');
      void qc.invalidateQueries({ queryKey: ['qr-tokens', token.objektId] });
    },
    onError: (err) => setError(err),
  });

  return (
    <tr className="text-sm">
      <td className="px-2 py-1 text-slate-700">{fmtDateShort(token.printedAt)}</td>
      <td className="px-2 py-1 text-slate-600">{fmtDateShort(token.expiresAt)}</td>
      <td className="px-2 py-1 text-right">
        {error instanceof Error && (
          <span className="text-xs text-red-600 mr-2">{error.message}</span>
        )}
        {askReason ? (
          <span className="inline-flex items-center gap-1">
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Grund (z.B. HV-Wechsel)"
            />
            <Button onClick={() => revoke.mutate()} disabled={revoke.isPending || !reason.trim()}>
              {revoke.isPending ? 'Widerrufe…' : 'Widerrufen'}
            </Button>
            <Button variant="ghost" onClick={() => setAskReason(false)}>
              Abbrechen
            </Button>
          </span>
        ) : (
          <Button variant="ghost" onClick={() => setAskReason(true)}>
            Widerrufen
          </Button>
        )}
      </td>
    </tr>
  );
}

function fmtDateShort(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

interface ObjektAnfrage {
  id: string;
  eingangAm: string;
  quelle: string;
  betreff: string | null;
  anfrageTyp: string | null;
  status: string;
  slaStatus: 'offen' | 'erfuellt' | 'verletzt' | null;
  slaReaktionBis: string | null;
}

function ObjektAnfragenPanel({
  anfragen,
  loading,
  error,
}: {
  anfragen: ObjektAnfrage[];
  loading: boolean;
  error: unknown;
}) {
  const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const recent = anfragen.filter((a) => new Date(a.eingangAm).getTime() >= cutoff);
  const verletzt30d = recent.filter((a) => a.slaStatus === 'verletzt').length;
  return (
    <Panel title="Anfragen / Inbox am Objekt">
      <div className="px-4 py-3">
        {error ? (
          <ErrorBanner error={error} />
        ) : loading ? (
          <div className="text-sm text-slate-500">lädt …</div>
        ) : anfragen.length === 0 ? (
          <div className="text-sm italic text-slate-500">Keine Anfragen für dieses Objekt.</div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-slate-600">
              <span>
                <strong className="text-slate-900">{recent.length}</strong> in den letzten 30 Tagen
              </span>
              {verletzt30d > 0 ? (
                <span className="rounded border border-red-200 bg-red-50 px-2 py-0.5 text-red-700">
                  ⚠ {verletzt30d} SLA verletzt
                </span>
              ) : null}
              <span className="ml-auto text-slate-400">Gesamt: {anfragen.length}</span>
            </div>
            <table className="w-full text-sm">
              <thead className="text-xs font-medium text-slate-600">
                <tr className="border-b border-slate-200">
                  <th className="py-1.5 text-left">Eingang</th>
                  <th className="py-1.5 text-left">Quelle</th>
                  <th className="py-1.5 text-left">Betreff</th>
                  <th className="py-1.5 text-left">Typ</th>
                  <th className="py-1.5 text-left">SLA</th>
                  <th className="py-1.5 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {anfragen.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-1.5 font-mono text-xs text-slate-600">
                      <Link
                        to="/inbox/$id"
                        params={{ id: a.id }}
                        className="text-brand-700 hover:underline"
                      >
                        {formatGermanDateFromTimestamp(a.eingangAm)}
                      </Link>
                    </td>
                    <td className="py-1.5 text-xs text-slate-700">{a.quelle}</td>
                    <td className="py-1.5">
                      <Link to="/inbox/$id" params={{ id: a.id }} className="hover:underline">
                        {a.betreff ?? <span className="italic text-slate-400">—</span>}
                      </Link>
                    </td>
                    <td className="py-1.5 text-slate-700">{a.anfrageTyp ?? '—'}</td>
                    <td className="py-1.5">
                      <ObjektSlaDot anfrage={a} />
                    </td>
                    <td className="py-1.5 text-xs text-slate-600">{a.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </Panel>
  );
}

function ObjektSlaDot({ anfrage }: { anfrage: ObjektAnfrage }) {
  if (!anfrage.slaStatus) return <span className="text-xs text-slate-400">—</span>;
  if (anfrage.slaStatus === 'verletzt') return <span className="text-red-600">●</span>;
  if (anfrage.slaStatus === 'erfuellt') return <span className="text-green-600">●</span>;
  if (anfrage.slaReaktionBis) {
    const restMs = msUntil(anfrage.slaReaktionBis);
    if (restMs <= 0) return <span className="text-red-600">●</span>;
  }
  return <span className="text-amber-600">●</span>;
}

function ObjektStammdatenPanel({ objekt, kunde }: { objekt: Objekt; kunde: Kunde | null }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [bezeichnung, setBezeichnung] = useState(objekt.bezeichnung);
  const [adresseStrasse, setAdresseStrasse] = useState(objekt.adresseStrasse ?? '');
  const [adressePlz, setAdressePlz] = useState(objekt.adressePlz ?? '');
  const [adresseOrt, setAdresseOrt] = useState(objekt.adresseOrt ?? '');
  const [typ, setTyp] = useState(objekt.typ ?? '');
  const [anzahlEinheiten, setAnzahlEinheiten] = useState(
    objekt.anzahlEinheiten != null ? String(objekt.anzahlEinheiten) : '',
  );
  const [hatAufzug, setHatAufzug] = useState(objekt.hatAufzug);
  const [baujahr, setBaujahr] = useState(objekt.baujahr != null ? String(objekt.baujahr) : '');
  const [notizen, setNotizen] = useState(objekt.notizen ?? '');
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: async () => {
      const patch: Record<string, unknown> = {};
      if (bezeichnung !== objekt.bezeichnung) patch['bezeichnung'] = bezeichnung;
      if ((objekt.adresseStrasse ?? '') !== adresseStrasse)
        patch['adresseStrasse'] = adresseStrasse;
      if ((objekt.adressePlz ?? '') !== adressePlz) patch['adressePlz'] = adressePlz;
      if ((objekt.adresseOrt ?? '') !== adresseOrt) patch['adresseOrt'] = adresseOrt;
      if ((objekt.typ ?? '') !== typ) patch['typ'] = typ;
      const newCount = anzahlEinheiten.trim() === '' ? null : Number(anzahlEinheiten);
      if ((objekt.anzahlEinheiten ?? null) !== newCount) patch['anzahlEinheiten'] = newCount;
      if (objekt.hatAufzug !== hatAufzug) patch['hatAufzug'] = hatAufzug;
      const newBaujahr = baujahr.trim() === '' ? null : Number(baujahr);
      if ((objekt.baujahr ?? null) !== newBaujahr) patch['baujahr'] = newBaujahr;
      if ((objekt.notizen ?? '') !== notizen) patch['notizen'] = notizen;
      if (Object.keys(patch).length === 0) return objekt;
      return api<{ item: Objekt }>(`/api/v1/objekte/${objekt.id}`, {
        method: 'PATCH',
        body: patch,
      });
    },
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['objekt', objekt.id] });
      void qc.invalidateQueries({ queryKey: ['objekte'] });
    },
    onError: (err) => setError(err),
  });

  return (
    <Panel
      title="Stammdaten"
      actions={
        !editing ? (
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Bearbeiten
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setBezeichnung(objekt.bezeichnung);
                setAdresseStrasse(objekt.adresseStrasse ?? '');
                setAdressePlz(objekt.adressePlz ?? '');
                setAdresseOrt(objekt.adresseOrt ?? '');
                setTyp(objekt.typ ?? '');
                setAnzahlEinheiten(
                  objekt.anzahlEinheiten != null ? String(objekt.anzahlEinheiten) : '',
                );
                setHatAufzug(objekt.hatAufzug);
                setBaujahr(objekt.baujahr != null ? String(objekt.baujahr) : '');
                setNotizen(objekt.notizen ?? '');
                setError(null);
                setEditing(false);
              }}
              disabled={save.isPending}
            >
              Abbrechen
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
        )
      }
    >
      <ErrorBanner error={error} />
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Field label="Kunde">
          {kunde ? (
            <Link
              to="/kunden/$id"
              params={{ id: kunde.id }}
              className="text-brand-700 hover:underline"
            >
              {kunde.kundennummer} — {kunde.firmenname}
            </Link>
          ) : (
            <span className="text-slate-400">Lade…</span>
          )}
        </Field>
        <Field label="Objektnummer">
          <span className="font-mono text-slate-600">{objekt.objektnummer ?? '–'}</span>
        </Field>
        <Field label="Bezeichnung">
          {editing ? (
            <Input value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} />
          ) : (
            <span>{objekt.bezeichnung}</span>
          )}
        </Field>
        <Field label="Typ">
          {editing ? (
            <Input value={typ} onChange={(e) => setTyp(e.target.value)} placeholder="Wohnhaus" />
          ) : (
            <span className="text-slate-600">{objekt.typ ?? '–'}</span>
          )}
        </Field>
        <Field label="Strasse">
          {editing ? (
            <Input value={adresseStrasse} onChange={(e) => setAdresseStrasse(e.target.value)} />
          ) : (
            <span className="text-slate-600">{objekt.adresseStrasse ?? '–'}</span>
          )}
        </Field>
        <Field label="PLZ / Ort">
          {editing ? (
            <div className="flex gap-2">
              <Input
                className="w-24"
                value={adressePlz}
                onChange={(e) => setAdressePlz(e.target.value)}
              />
              <Input value={adresseOrt} onChange={(e) => setAdresseOrt(e.target.value)} />
            </div>
          ) : (
            <span className="text-slate-600">
              {[objekt.adressePlz, objekt.adresseOrt].filter(Boolean).join(' ') || '–'}
            </span>
          )}
        </Field>
        <Field label="Anzahl Einheiten">
          {editing ? (
            <Input
              type="number"
              min="1"
              value={anzahlEinheiten}
              onChange={(e) => setAnzahlEinheiten(e.target.value)}
            />
          ) : (
            <span className="text-slate-600 tabular-nums">{objekt.anzahlEinheiten ?? '–'}</span>
          )}
        </Field>
        <Field label="Aufzug">
          {editing ? (
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={hatAufzug}
                onChange={(e) => setHatAufzug(e.target.checked)}
              />
              vorhanden
            </label>
          ) : (
            <span className="text-slate-600">{objekt.hatAufzug ? 'ja' : 'nein'}</span>
          )}
        </Field>
        <Field label="Baujahr">
          {editing ? (
            <Input
              type="number"
              min="1800"
              max="2100"
              value={baujahr}
              onChange={(e) => setBaujahr(e.target.value)}
              placeholder="z. B. 1985"
            />
          ) : (
            <span className="text-slate-600 tabular-nums">{objekt.baujahr ?? '–'}</span>
          )}
        </Field>
        <Field label="Angelegt">
          <span className="text-slate-500">{formatGermanDateFromTimestamp(objekt.createdAt)}</span>
        </Field>
        <div className="md:col-span-2">
          <Field label="Notizen">
            {editing ? (
              <textarea
                value={notizen}
                onChange={(e) => setNotizen(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            ) : (
              <span className="text-slate-600 whitespace-pre-wrap">{objekt.notizen ?? '–'}</span>
            )}
          </Field>
        </div>
      </dl>
    </Panel>
  );
}

function KontakteList({ kontakte }: { kontakte: Kontakt[] }) {
  const sorted = [...kontakte].sort((a, b) => {
    if (a.istHauptkontakt !== b.istHauptkontakt) return a.istHauptkontakt ? -1 : 1;
    return a.nachname.localeCompare(b.nachname);
  });
  return (
    <ul className="space-y-2 text-sm">
      {sorted.map((k) => (
        <li
          key={k.id}
          className="flex items-start justify-between rounded-md border border-slate-100 bg-slate-50/50 px-3 py-2"
        >
          <div>
            <div className="font-medium">
              {[k.vorname, k.nachname].filter(Boolean).join(' ')}
              {k.istHauptkontakt && (
                <span className="ml-2 inline-flex items-center rounded-md border bg-amber-100 text-amber-800 border-amber-200 px-1.5 py-0.5 text-xs font-medium">
                  Haupt
                </span>
              )}
            </div>
            {k.rolle && <div className="text-xs text-slate-500">{k.rolle}</div>}
          </div>
          <div className="text-xs text-slate-600 text-right">
            {k.email && <div>{k.email}</div>}
            {k.telefon && <div>{k.telefon}</div>}
            {k.mobil && <div>{k.mobil}</div>}
          </div>
        </li>
      ))}
    </ul>
  );
}

function Panel({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {actions}
      </header>
      <div className="p-4 space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slice 4 (CEO 2026-05-04): Rechnungsadresse / Eingänge & Flächen / CC-Mails
// Plan: docs/superpowers/plans/2026-05-03-stammdaten-erweiterung.md §7.2
// ---------------------------------------------------------------------------

type RechnungsempfaengerTyp = 'kunde' | 'eigentuemer' | 'hausverwaltung' | 'freie_anschrift';

const EMPF_OPTIONS: { value: RechnungsempfaengerTyp; label: string; hint: string }[] = [
  { value: 'kunde', label: 'Wie Kunde', hint: 'Adresse aus Kunden-Stammdaten' },
  { value: 'eigentuemer', label: 'Eigentümer', hint: 'Adresse aus verknüpftem Eigentümer-Kunden' },
  { value: 'hausverwaltung', label: 'Hausverwaltung', hint: 'Adresse aus verknüpfter HV' },
  { value: 'freie_anschrift', label: 'Freie c/o-Anschrift', hint: 'Direkt eingegebene Adresse' },
];

function ObjektRechnungsadressePanel({ objekt }: { objekt: Objekt }) {
  const qc = useQueryClient();
  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () =>
      api<{ items: { id: string; kundennummer: string; firmenname: string | null }[] }>(
        '/api/v1/kunden',
      ),
  });

  const [empfTyp, setEmpfTyp] = useState<RechnungsempfaengerTyp>(objekt.rechnungsempfaengerTyp);
  const [eigKid, setEigKid] = useState(objekt.eigentuemerKundeId ?? '');
  const [hvKid, setHvKid] = useState(objekt.hausverwaltungKundeId ?? '');
  const [coZeile, setCoZeile] = useState(objekt.rechnungCoZeile ?? '');
  const [name, setName] = useState(objekt.rechnungAnschriftName ?? '');
  const [zusatz, setZusatz] = useState(objekt.rechnungAnschriftZusatz ?? '');
  const [strasse, setStrasse] = useState(objekt.rechnungAnschriftStrasse ?? '');
  const [plz, setPlz] = useState(objekt.rechnungAnschriftPlz ?? '');
  const [ort, setOrt] = useState(objekt.rechnungAnschriftOrt ?? '');
  const [land, setLand] = useState(objekt.rechnungAnschriftLand ?? 'DE');
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: () => {
      const patch: Record<string, unknown> = { rechnungsempfaengerTyp: empfTyp };
      patch['eigentuemerKundeId'] = empfTyp === 'eigentuemer' ? eigKid || null : eigKid || null;
      patch['hausverwaltungKundeId'] = hvKid || null;
      if (empfTyp === 'freie_anschrift') {
        patch['rechnungCoZeile'] = coZeile.trim() || null;
        patch['rechnungAnschriftName'] = name.trim();
        patch['rechnungAnschriftZusatz'] = zusatz.trim() || null;
        patch['rechnungAnschriftStrasse'] = strasse.trim();
        patch['rechnungAnschriftPlz'] = plz.trim();
        patch['rechnungAnschriftOrt'] = ort.trim();
        patch['rechnungAnschriftLand'] = land.trim() || 'DE';
      }
      return api<{ item: Objekt }>(`/api/v1/objekte/${objekt.id}`, {
        method: 'PATCH',
        body: patch,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['objekt', objekt.id] });
      void qc.invalidateQueries({ queryKey: ['objekte'] });
    },
    onError: (err) => setError(err),
  });

  const kunden = kundenQ.data?.items ?? [];

  return (
    <Panel
      title="Rechnungsadresse"
      actions={
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Speichere…' : 'Speichern'}
        </Button>
      }
    >
      <ErrorBanner error={error} />
      <div className="space-y-4">
        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-slate-500">Empfänger-Typ</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {EMPF_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm ${
                  empfTyp === opt.value
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="empfTyp"
                  value={opt.value}
                  checked={empfTyp === opt.value}
                  onChange={() => setEmpfTyp(opt.value)}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium text-slate-900">{opt.label}</span>
                  <span className="block text-xs text-slate-500">{opt.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Eigentümer (Kunde)">
            <Select
              value={eigKid}
              onChange={(e) => setEigKid(e.target.value)}
              disabled={empfTyp !== 'eigentuemer' && !eigKid}
            >
              <option value="">– keiner –</option>
              {kunden.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.kundennummer} — {k.firmenname ?? '(Privat)'}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Hausverwaltung (Kunde)">
            <Select
              value={hvKid}
              onChange={(e) => setHvKid(e.target.value)}
              disabled={empfTyp !== 'hausverwaltung' && !hvKid}
            >
              <option value="">– keine –</option>
              {kunden.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.kundennummer} — {k.firmenname ?? '(Privat)'}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {empfTyp === 'freie_anschrift' && (
          <fieldset className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
            <legend className="text-xs font-medium text-slate-700">
              Freie c/o-Anschrift (Pflicht: Name, Straße, PLZ, Ort)
            </legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="c/o-Zeile">
                <Input
                  value={coZeile}
                  onChange={(e) => setCoZeile(e.target.value)}
                  placeholder="c/o Hausverwaltung Mustermann"
                />
              </Field>
              <Field label="Name *">
                <Input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Empfänger-Name oder Firma"
                />
              </Field>
              <Field label="Adresszusatz">
                <Input
                  value={zusatz}
                  onChange={(e) => setZusatz(e.target.value)}
                  placeholder="z. Hd. Frau Müller"
                />
              </Field>
              <Field label="Straße *">
                <Input required value={strasse} onChange={(e) => setStrasse(e.target.value)} />
              </Field>
              <Field label="PLZ *">
                <Input required value={plz} onChange={(e) => setPlz(e.target.value)} />
              </Field>
              <Field label="Ort *">
                <Input required value={ort} onChange={(e) => setOrt(e.target.value)} />
              </Field>
              <Field label="Land">
                <Input value={land} onChange={(e) => setLand(e.target.value)} placeholder="DE" />
              </Field>
            </div>
          </fieldset>
        )}
      </div>
    </Panel>
  );
}

interface EingangDraft {
  reihenfolge: number | null;
  bezeichnung: string;
  flaecheQm: string;
  etagen: string;
  notizen: string;
}

function eingangFromServer(e: Eingang): EingangDraft {
  return {
    reihenfolge: e.reihenfolge,
    bezeichnung: e.bezeichnung ?? '',
    flaecheQm: e.flaecheQm ?? '',
    etagen: e.etagen != null ? String(e.etagen) : '',
    notizen: e.notizen ?? '',
  };
}

function ObjektEingaengePanel({ objekt }: { objekt: Objekt }) {
  const qc = useQueryClient();
  const eingaengeQ = useQuery({
    queryKey: ['eingaenge', objekt.id],
    queryFn: () => api<{ items: Eingang[] }>(`/api/v1/objekte/${objekt.id}/eingaenge`),
  });
  const [draftEingaenge, setDraftEingaenge] = useState<EingangDraft[] | null>(null);

  // Server-State in Editor-Drafts übersetzen, sobald nicht im Edit-Modus.
  const editing = draftEingaenge !== null;
  const items: EingangDraft[] =
    draftEingaenge ?? (eingaengeQ.data?.items ?? []).map(eingangFromServer);

  const [aussenflaecheQm, setAussenflaecheQm] = useState(objekt.aussenflaecheQm ?? '');
  const [gruenflaecheQm, setGruenflaecheQm] = useState(objekt.gruenflaecheQm ?? '');
  const [winterflaecheQm, setWinterflaecheQm] = useState(objekt.winterdienstflaecheQm ?? '');
  // BV-Pricing-Eigenschaften (Migration 0057): Klassen-Selects + Frequenz-Select.
  // Leerstring '' im UI mappt auf null beim Save (Pricing-Vorschlag dann disabled).
  type Klasse = 'klein' | 'mittel' | 'gross' | '';
  type Freq = '0.5' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '';
  const [flurgroesseKlasse, setFlurgroesseKlasse] = useState<Klasse>(
    objekt.flurgroesseKlasse ?? '',
  );
  const [hausGroesseKlasse, setHausGroesseKlasse] = useState<Klasse>(
    objekt.hausGroesseKlasse ?? '',
  );
  const [reinigungProWoche, setReinigungProWoche] = useState<Freq>(
    objekt.reinigungProWoche != null ? (Number(objekt.reinigungProWoche).toString() as Freq) : '',
  );
  const [error, setError] = useState<unknown>(null);

  const saveEingaenge = useMutation({
    mutationFn: (drafts: EingangDraft[]) => {
      const payload = drafts.map((e, i) => {
        const out: Record<string, unknown> = { reihenfolge: e.reihenfolge ?? i + 1 };
        if (e.bezeichnung.trim()) out['bezeichnung'] = e.bezeichnung.trim();
        if (e.flaecheQm.trim()) out['flaecheQm'] = e.flaecheQm.trim();
        if (e.etagen.trim()) out['etagen'] = Number(e.etagen);
        if (e.notizen.trim()) out['notizen'] = e.notizen.trim();
        return out;
      });
      return api<{ items: Eingang[] }>(`/api/v1/objekte/${objekt.id}/eingaenge`, {
        method: 'PUT',
        body: { eingaenge: payload },
      });
    },
    onSuccess: () => {
      setDraftEingaenge(null);
      void qc.invalidateQueries({ queryKey: ['eingaenge', objekt.id] });
    },
    onError: (err) => setError(err),
  });

  const saveFlaechen = useMutation({
    mutationFn: async () => {
      const patch: Record<string, unknown> = {};
      const norm = (v: string) => (v.trim() === '' ? null : v.trim());
      if ((objekt.aussenflaecheQm ?? '') !== aussenflaecheQm)
        patch['aussenflaecheQm'] = norm(aussenflaecheQm);
      if ((objekt.gruenflaecheQm ?? '') !== gruenflaecheQm)
        patch['gruenflaecheQm'] = norm(gruenflaecheQm);
      if ((objekt.winterdienstflaecheQm ?? '') !== winterflaecheQm)
        patch['winterdienstflaecheQm'] = norm(winterflaecheQm);
      if ((objekt.flurgroesseKlasse ?? '') !== flurgroesseKlasse)
        patch['flurgroesseKlasse'] = flurgroesseKlasse === '' ? null : flurgroesseKlasse;
      if ((objekt.hausGroesseKlasse ?? '') !== hausGroesseKlasse)
        patch['hausGroesseKlasse'] = hausGroesseKlasse === '' ? null : hausGroesseKlasse;
      const currentFreq =
        objekt.reinigungProWoche != null ? Number(objekt.reinigungProWoche).toString() : '';
      if (currentFreq !== reinigungProWoche)
        patch['reinigungProWoche'] = reinigungProWoche === '' ? null : reinigungProWoche;
      if (Object.keys(patch).length === 0) return { item: objekt };
      return api<{ item: Objekt }>(`/api/v1/objekte/${objekt.id}`, {
        method: 'PATCH',
        body: patch,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['objekt', objekt.id] });
    },
    onError: (err) => setError(err),
  });

  function ensureDraft(): EingangDraft[] {
    return draftEingaenge ?? items.map((e) => ({ ...e }));
  }
  function setItem(idx: number, patch: Partial<EingangDraft>) {
    const base = ensureDraft();
    const next = base.map((e, i) => (i === idx ? { ...e, ...patch } : e));
    setDraftEingaenge(next);
  }
  function addItem() {
    setDraftEingaenge([
      ...ensureDraft(),
      { reihenfolge: null, bezeichnung: '', flaecheQm: '', etagen: '', notizen: '' },
    ]);
  }
  function removeItem(idx: number) {
    setDraftEingaenge(ensureDraft().filter((_, i) => i !== idx));
  }
  function moveItem(idx: number, delta: number) {
    const base = ensureDraft();
    const target = idx + delta;
    if (target < 0 || target >= base.length) return;
    const next = [...base];
    const tmp = next[idx]!;
    next[idx] = next[target]!;
    next[target] = tmp;
    // reihenfolge zurücksetzen, damit der UNIQUE-Index nicht knallt
    setDraftEingaenge(next.map((e, i) => ({ ...e, reihenfolge: i + 1 })));
  }

  return (
    <div className="space-y-6">
      <Panel
        title={`Eingänge (${items.length})`}
        actions={
          <div className="flex gap-2">
            {editing && (
              <Button variant="ghost" onClick={() => setDraftEingaenge(null)}>
                Abbrechen
              </Button>
            )}
            <Button onClick={addItem} variant="ghost">
              + Eingang
            </Button>
            <Button
              onClick={() => saveEingaenge.mutate(draftEingaenge ?? items)}
              disabled={saveEingaenge.isPending || !editing}
            >
              {saveEingaenge.isPending ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
        }
      >
        <ErrorBanner error={error ?? eingaengeQ.error} />
        {items.length === 0 ? (
          <div className="text-sm text-slate-400">
            Noch keine Eingänge. Klick „+ Eingang" zum Hinzufügen.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1 text-left w-10">#</th>
                <th className="px-2 py-1 text-left">Bezeichnung</th>
                <th className="px-2 py-1 text-right w-28">Fläche m²</th>
                <th className="px-2 py-1 text-right w-20">Etagen</th>
                <th className="px-2 py-1 text-left">Notizen</th>
                <th className="px-2 py-1 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((e, idx) => (
                <tr key={idx}>
                  <td className="px-2 py-1 font-mono text-xs text-slate-500">{idx + 1}</td>
                  <td className="px-2 py-1">
                    <Input
                      value={e.bezeichnung}
                      onChange={(ev) => setItem(idx, { bezeichnung: ev.target.value })}
                      placeholder="Haupteingang"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      type="number"
                      step="0.01"
                      value={e.flaecheQm}
                      onChange={(ev) => setItem(idx, { flaecheQm: ev.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      type="number"
                      min="0"
                      value={e.etagen}
                      onChange={(ev) => setItem(idx, { etagen: ev.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      value={e.notizen}
                      onChange={(ev) => setItem(idx, { notizen: ev.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      className="px-1 text-slate-500 hover:text-slate-900"
                      onClick={() => moveItem(idx, -1)}
                      title="nach oben"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="px-1 text-slate-500 hover:text-slate-900"
                      onClick={() => moveItem(idx, 1)}
                      title="nach unten"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="ml-2 text-red-600 hover:underline text-xs"
                      onClick={() => removeItem(idx)}
                    >
                      entfernen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel
        title="Flächen am Objekt"
        actions={
          <Button onClick={() => saveFlaechen.mutate()} disabled={saveFlaechen.isPending}>
            {saveFlaechen.isPending ? 'Speichere…' : 'Speichern'}
          </Button>
        }
      >
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Field label="Außenfläche m²">
            <Input
              type="number"
              step="0.01"
              value={aussenflaecheQm}
              onChange={(e) => setAussenflaecheQm(e.target.value)}
            />
          </Field>
          <Field label="Grünfläche m²">
            <Input
              type="number"
              step="0.01"
              value={gruenflaecheQm}
              onChange={(e) => setGruenflaecheQm(e.target.value)}
            />
          </Field>
          <Field label="Winterdienstfläche m²">
            <Input
              type="number"
              step="0.01"
              value={winterflaecheQm}
              onChange={(e) => setWinterflaecheQm(e.target.value)}
            />
          </Field>
        </dl>
      </Panel>

      <Panel
        title="BV-Pricing-Eigenschaften"
        actions={
          <Button onClick={() => saveFlaechen.mutate()} disabled={saveFlaechen.isPending}>
            {saveFlaechen.isPending ? 'Speichere…' : 'Speichern'}
          </Button>
        }
      >
        <p className="mb-3 text-xs text-slate-500">
          Steuern den Auto-Pricing-Vorschlag im BV-Wizard. Hausgröße wirkt auf die
          Haustechnik-Pauschale je Eingang, Flurgröße + Reinigung pro Woche auf die
          Innenreinigungs-Pauschale je Einheit.
        </p>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Field label="Hausgröße (Haustechnik)">
            <Select
              value={hausGroesseKlasse}
              onChange={(e) => setHausGroesseKlasse(e.target.value as Klasse)}
            >
              <option value="">— nicht gesetzt —</option>
              <option value="klein">klein</option>
              <option value="mittel">mittel</option>
              <option value="gross">groß</option>
            </Select>
          </Field>
          <Field label="Flurgröße (Innenreinigung)">
            <Select
              value={flurgroesseKlasse}
              onChange={(e) => setFlurgroesseKlasse(e.target.value as Klasse)}
            >
              <option value="">— nicht gesetzt —</option>
              <option value="klein">klein</option>
              <option value="mittel">mittel</option>
              <option value="gross">groß</option>
            </Select>
          </Field>
          <Field label="Reinigung pro Woche">
            <Select
              value={reinigungProWoche}
              onChange={(e) => setReinigungProWoche(e.target.value as Freq)}
            >
              <option value="">— nicht gesetzt —</option>
              <option value="0.5">0,5× (alle 14 Tage)</option>
              <option value="1">1×</option>
              <option value="2">2×</option>
              <option value="3">3×</option>
              <option value="4">4×</option>
              <option value="5">5×</option>
              <option value="6">6×</option>
              <option value="7">7×</option>
            </Select>
          </Field>
        </dl>
      </Panel>
    </div>
  );
}

function ObjektCcMailsPanel({ objekt }: { objekt: Objekt }) {
  const qc = useQueryClient();
  const [mails, setMails] = useState<string[]>(objekt.ccMails);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: () =>
      api<{ item: Objekt }>(`/api/v1/objekte/${objekt.id}`, {
        method: 'PATCH',
        body: { ccMails: mails },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['objekt', objekt.id] }),
    onError: (err) => setError(err),
  });

  function addMail() {
    const v = draft.trim();
    if (!v) return;
    if (mails.includes(v)) {
      setDraft('');
      return;
    }
    setMails([...mails, v]);
    setDraft('');
  }

  return (
    <Panel
      title={`CC-Mails (${mails.length})`}
      actions={
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Speichere…' : 'Speichern'}
        </Button>
      }
    >
      <ErrorBanner error={error} />
      <p className="text-xs text-slate-500">
        Diese Adressen erhalten Rechnungen und Auftragsbestätigungen zu diesem Objekt zusätzlich in
        Kopie.
      </p>
      <div className="flex flex-wrap gap-2">
        {mails.map((m) => (
          <span
            key={m}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm"
          >
            {m}
            <button
              type="button"
              className="text-slate-400 hover:text-red-600"
              onClick={() => setMails(mails.filter((x) => x !== m))}
              aria-label={`${m} entfernen`}
            >
              ×
            </button>
          </span>
        ))}
        {mails.length === 0 && (
          <span className="text-sm text-slate-400">Keine CC-Mails hinterlegt.</span>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addMail();
            }
          }}
          placeholder="hausverwaltung@example.de"
        />
        <Button variant="ghost" onClick={addMail}>
          Hinzufügen
        </Button>
      </div>
    </Panel>
  );
}
